import { useState } from "react";
import { getLocalEntries, getUser } from "@/store";
import { requestMagicLink } from "@/api";

export default function ProtectBanner({ onAuthSuccess, onDismiss }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    if (!email.includes("@")) { setErr("Enter a valid email."); return; }
    setLoading(true); setErr("");
    try {
      await requestMagicLink(email, getUser().name || "", getLocalEntries());
      setSent(true);
    } catch {
      // Duplicate email is fine — still show success
      setSent(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      data-testid="protect-banner"
      style={{
        marginBottom: 16,
        background: "rgba(29,158,117,0.08)",
        border: "1px solid var(--teal-border)",
        borderRadius: 10,
        padding: "14px 16px",
        animation: "fadeRise 0.4s ease",
      }}
    >
      {!sent ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div>
              <p style={{
                fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
                letterSpacing: "0.12em", color: "var(--teal)",
                textTransform: "uppercase", marginBottom: 3,
              }}>
                Protect your entries
              </p>
              <p style={{ fontSize: 14, color: "var(--cream-dim)", lineHeight: 1.55 }}>
                You've written 5 entries. Add your email to back them up.
              </p>
            </div>
            <button
              onClick={onDismiss}
              data-testid="protect-banner-dismiss"
              style={{
                background: "none", border: "none",
                color: "var(--cream-faint)",
                fontSize: 20, lineHeight: 1,
                cursor: "pointer",
                padding: "0 0 0 12px",
                flexShrink: 0,
                minWidth: 44, minHeight: 44,
                display: "flex", alignItems: "center", justifyContent: "flex-end",
              }}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="your@email.com"
              autoComplete="email"
              data-testid="protect-banner-email"
              style={{
                fontSize: 16,
                background: "rgba(29,158,117,0.08)",
                border: `1px solid ${err ? "rgba(226,75,74,0.5)" : "var(--teal-border)"}`,
                borderRadius: 7,
                padding: "0 14px",
                height: 44,
                color: "var(--cream)",
                outline: "none",
                width: "100%",
                fontFamily: "var(--mono)",
              }}
            />
            <button
              onClick={submit}
              disabled={loading}
              data-testid="protect-banner-submit"
              style={{
                height: 44,
                background: "var(--teal)", color: "var(--deep)",
                border: "none", borderRadius: 7,
                fontFamily: "var(--mono)", fontSize: 13, fontWeight: 400,
                letterSpacing: "0.05em",
                cursor: loading ? "default" : "pointer",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? "..." : "Back up my entries"}
            </button>
          </div>

          {err && (
            <p style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300, color: "rgba(226,75,74,0.8)", marginTop: 6 }}>
              {err}
            </p>
          )}
        </>
      ) : (
        <div data-testid="protect-banner-success">
          <p style={{
            fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
            letterSpacing: "0.12em", color: "var(--teal)",
            textTransform: "uppercase", marginBottom: 6,
          }}>
            Email saved
          </p>
          <p style={{ fontSize: 14, color: "var(--cream-dim)", lineHeight: 1.65 }}>
            We saved <strong style={{ fontWeight: 500, color: "var(--cream)" }}>{email}</strong>.
            We'll email you when cloud backup is available. Your entries are safe on this device.
          </p>
        </div>
      )}
    </div>
  );
}
